import { apiClient } from '@/api/client';
import { Cuisine } from '@/types/cuisine';
import { Paginated } from '@/types/pagination';
import { mapCuisineWireToDomain } from '../mappers/cuisine';
import {
  cuisineWireSchema,
  paginatedCuisineResponseSchema,
  type PaginatedCuisineResponseWire,
} from '../schemas/cuisine';

/**
 * Domain-oriented API module for cuisine reference data.
 * Read-only: cuisines are curated reference data served by the Gateway.
 */

export interface ListCuisinesParams {
  page?: number;
  per_page?: number;
  /** Case-insensitive search across name and slug. */
  query?: string;
}

function mapPaginatedCuisineWireToDomain(
  wire: PaginatedCuisineResponseWire,
): Paginated<Cuisine> {
  return {
    items: wire.items.map(mapCuisineWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

function buildQueryString(params: ListCuisinesParams): string {
  const queryParams: string[] = [];
  if (params.page !== undefined) queryParams.push(`page=${params.page}`);
  if (params.per_page !== undefined) queryParams.push(`per_page=${params.per_page}`);
  if (params.query !== undefined && params.query !== '') {
    queryParams.push(`query=${encodeURIComponent(params.query)}`);
  }
  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

export async function listCuisines(
  params: ListCuisinesParams = {},
): Promise<Paginated<Cuisine>> {
  const queryString = buildQueryString(params);
  const endpoint = `/api/v1/cuisines${queryString}`;
  const wire = await apiClient.get<unknown>(endpoint);
  const validated = paginatedCuisineResponseSchema.parse(wire);
  return mapPaginatedCuisineWireToDomain(validated);
}

export async function getCuisine(slug: string): Promise<Cuisine> {
  const wire = await apiClient.get<unknown>(`/api/v1/cuisines/${slug}`);
  const validated = cuisineWireSchema.parse(wire);
  return mapCuisineWireToDomain(validated);
}
